import React, { createContext, useState, useContext, useEffect } from 'react';
import { useMemo } from 'react';
export const PaxContext = createContext(null);

// Utility function to get stored data from localStorage
const getStoredData = (key, defaultValue) => {
  if (typeof window !== 'undefined') {
    const storedData = localStorage?.getItem(key);
    try {
      // Special handling for currency since it's a simple string
      if (key === 'currency') {
        return storedData || defaultValue;
      }
      // For other JSON objects
      return storedData ? JSON.parse(storedData) : defaultValue;
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
      return defaultValue;
    }
  }
  return defaultValue;
};

export const PaxProvider = ({ children }) => {
  const [pax, setPax] = useState(() => getStoredData('pax', {}));
  const [tour, setTour] = useState(() => getStoredData('tour', {}));
  const [user, setUser] = useState(() => getStoredData('user', {}));
  const [blogCity, setBlogCity] = useState(() =>
    getStoredData('blogCity', null)
  );
  const [bestReview, setBestReview] = useState(() =>
    getStoredData('bestReview', '')
  );
  const [currency, setCurrency] = useState(() =>
    getStoredData('currency', 'USD')
  );

  // Add checkout step state
  const [checkoutSteps, setCheckoutSteps] = useState(() =>
    getStoredData('checkoutSteps', {
      currentStep: 1,
      contactDetailsComplete: false,
      paymentDetailsComplete: false
    })
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('pax', JSON?.stringify(pax));
      localStorage.setItem('tour', JSON?.stringify(tour));
      localStorage.setItem('user', JSON?.stringify(user));
      localStorage.setItem('blogCity', JSON?.stringify(blogCity));
      localStorage.setItem('bestReview', JSON?.stringify(bestReview));
      localStorage.setItem('currency', currency);
      localStorage.setItem('checkoutSteps', JSON.stringify(checkoutSteps));
    }
  }, [pax, tour, user, blogCity, bestReview, currency, checkoutSteps]);

  const updatePax = newObject1 => {
    setPax(prev => ({
      ...prev,
      ...newObject1
    }));
  };

  const updateTour = newObject2 => {
    setTour(newObject2);
  };

  const updateUser = newObject3 => {
    setUser(newObject3);
  };

  const updateBestReview = newObject5 => {
    setBestReview(newObject5);
  };

  const updateBlogCity = newObject4 => {
    setBlogCity(newObject4);
  };

  const updateCurrency = newCurrency => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('currency', newCurrency);
    }
    setCurrency(newCurrency);
  };

  // Function to update checkout steps
  const updateCheckoutSteps = steps => {
    setCheckoutSteps(prevSteps => ({
      ...prevSteps,
      ...steps
    }));
  };

  const contextValue = useMemo(
    () => ({
      pax,
      tour,
      user,
      blogCity,
      bestReview,
      updatePax,
      updateTour,
      updateUser,
      updateBlogCity,
      updateBestReview,
      currency,
      updateCurrency,
      checkoutSteps,
      updateCheckoutSteps
    }),
    [pax, tour, user, blogCity, bestReview, currency, checkoutSteps]
  );
  return (
    <PaxContext.Provider value={contextValue}>{children}</PaxContext.Provider>
  );
};

// Add a custom hook to use the context
export const useCheckoutContext = () => useContext(PaxContext);
